import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloudNavLayout, ElementVisibility, Env, HostName, SideNavVariant } from '@lg-private/cloud-nav';

import { redirectTo as redirectToAction } from 'baas-ui/actions';
import { EnvironmentDesc } from 'baas-ui/common/constants';
import { GLOBAL_MONITORING_ADMIN, GLOBAL_OWNER } from 'baas-ui/constants';
import { AsyncDispatch } from 'baas-ui/redux_util';
import { getHomeState, getSettingsState, getUserProfileState } from 'baas-ui/selectors';
import { RootState } from 'baas-ui/types';
import { RoleAssignment } from 'admin-sdk';

import { getAllowedDeprecatedFeatures } from './actions';
import useCloudNavProjectSync from './useCloudNavProjectSync';

export const getNavEnv = (environment: EnvironmentDesc, cloudUIBaseUrl?: string): Env => {
  switch (environment) {
    case EnvironmentDesc.Prod:
      return Env.Prod;
    case EnvironmentDesc.Staging:
      return Env.Staging;
    case EnvironmentDesc.QA:
      return Env.QA;
    case EnvironmentDesc.Dev:
      return Env.Dev;
    default:
      return cloudUIBaseUrl ? Env.MmsLocal : Env.Test;
  }
};

export interface Props {
  children: ReactElement;
}

const CloudNavProjectSync = () => {
  useCloudNavProjectSync();
  return null;
};

const Nav = ({ children }: Props) => {
  const {
    cloudUIBaseUrl,
    environmentDesc,
    showCloudNav = false,
  } = useSelector((state: RootState) => getSettingsState(state));
  const { groupId } = useSelector((state: RootState) => getHomeState(state));
  const userProfile = useSelector((state: RootState) => getUserProfileState(state));
  const dispatch = useDispatch<AsyncDispatch>();
  const [showPrimaryNavDataAPI, setShowPrimaryNavDataAPI] = useState(false);
  const [showPrimaryNavSync, setShowPrimaryNavSync] = useState(false);
  const isGlobalMonitoringAdmin = userProfile?.roles?.some((role: RoleAssignment) =>
    [GLOBAL_MONITORING_ADMIN, GLOBAL_OWNER].includes(role.roleName)
  );

  useEffect(() => {
    (async () => {
      if (groupId) {
        try {
          const allowedFeatures = await dispatch(getAllowedDeprecatedFeatures({ groupId }));
          if (allowedFeatures) {
            const { isDataAPIAllowed, isSyncAllowed } = allowedFeatures;
            setShowPrimaryNavDataAPI(isDataAPIAllowed);
            setShowPrimaryNavSync(isSyncAllowed);
          }
        } catch {
          setShowPrimaryNavDataAPI(false);
          setShowPrimaryNavSync(false);
        }
      }
    })();
  }, [groupId]);

  if (!showCloudNav) {
    return children;
  }

  return (
    <CloudNavLayout
      // in version 0.0.48/49, the default z-index causes LG menus/selects to render below the child element
      style={{ zIndex: 0 }}
      env={getNavEnv(environmentDesc, cloudUIBaseUrl)}
      currentHost={HostName.BAAS}
      elementVisibility={{
        topNav: {
          utilities: {
            internal_admin: {
              root: isGlobalMonitoringAdmin ? ElementVisibility.Enabled : ElementVisibility.Hidden,
            },
          },
        },
        sideNav: {
          primary: {
            data_api: showPrimaryNavDataAPI ? ElementVisibility.Enabled : ElementVisibility.Hidden,
            device_sync: showPrimaryNavSync ? ElementVisibility.Enabled : ElementVisibility.Hidden,
          },
        },
      }}
      onNavigation={(event, { host, path }) => {
        if (host === HostName.BAAS) {
          event.preventDefault();
          event.stopPropagation();
          dispatch(redirectToAction(path));
        }
      }}
      sideNavVariant={SideNavVariant.Project}
    >
      <CloudNavProjectSync />
      {children}
    </CloudNavLayout>
  );
};

export default Nav;
